export const DEAL_STATUS_LABELS = {
  open: "Aberto",
  won: "Ganho",
  lost: "Perdido",
};

export const ACTIVITY_STATUS_LABELS = {
  pending: "Pendente",
  done: "Concluida",
  canceled: "Cancelada",
};

export const ACTIVITY_TYPE_LABELS = {
  task: "Tarefa",
  call: "Ligacao",
  meeting: "Reuniao",
  email: "E-mail",
  note: "Nota",
  follow_up: "Follow-up",
};

export const canManageCrmPipeline = (user) => user?.profile === "admin";

export const CRM_REALTIME_VERSION = 1;

const CRM_REALTIME_RESOURCES = ["pipeline", "stage", "deal", "activity"];
const CRM_REALTIME_ACTIONS = ["create", "update", "delete", "move", "outcome"];

export const getCrmRealtimeRefreshPlan = ({
  event,
  companyId,
  selectedPipelineId,
  detailDealId,
} = {}) => {
  const isCompatible =
    event?.version === CRM_REALTIME_VERSION &&
    Number(event?.companyId) === Number(companyId) &&
    CRM_REALTIME_RESOURCES.includes(event?.resource) &&
    CRM_REALTIME_ACTIONS.includes(event?.action);

  if (!isCompatible) {
    return { ignore: true };
  }

  const isPipelineResource = ["pipeline", "stage"].includes(event.resource);
  const isDealResource = event.resource === "deal";
  const isActivityResource = event.resource === "activity";
  const affectedDealId = isDealResource ? event.entityId : event.dealId;
  const affectsOpenDetail =
    Boolean(detailDealId) && Number(affectedDealId) === Number(detailDealId);
  const affectsSelectedPipeline =
    !event.pipelineId ||
    !selectedPipelineId ||
    Number(event.pipelineId) === Number(selectedPipelineId);
  const closeDetail =
    isDealResource && event.action === "delete" && affectsOpenDetail;

  return {
    ignore: false,
    reloadPipelines: isPipelineResource,
    reloadDeals:
      isActivityResource || (isDealResource && affectsSelectedPipeline),
    reloadDetail:
      !closeDetail &&
      Boolean(detailDealId) &&
      (affectsOpenDetail || event.resource === "stage"),
    closeDetail,
  };
};

export const sortByOrderAndId = (items = []) =>
  [...items].sort((first, second) => {
    const firstOrder = Number(first?.order ?? 0);
    const secondOrder = Number(second?.order ?? 0);

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return Number(first?.id ?? 0) - Number(second?.id ?? 0);
  });

export const normalizePipelineStages = (pipeline) =>
  sortByOrderAndId(pipeline?.stages || []);

export const groupDealsByStage = (deals = []) =>
  deals.reduce((grouped, deal) => {
    const stageId = String(deal?.stageId || "without-stage");

    return {
      ...grouped,
      [stageId]: [...(grouped[stageId] || []), deal],
    };
  }, {});

export const getActiveStages = (stages = []) =>
  stages.filter((stage) => stage?.isActive !== false);

export const getDefaultStageId = (pipeline) => {
  const stages = getActiveStages(normalizePipelineStages(pipeline));

  return stages[0]?.id || "";
};

export const mergeDealsById = (currentDeals = [], incomingDeals = []) => {
  const dealsById = new Map(
    currentDeals.map((deal) => [String(deal.id), deal]),
  );

  incomingDeals.forEach((deal) => {
    dealsById.set(String(deal.id), deal);
  });

  return Array.from(dealsById.values());
};

const toDateBoundary = (value, time) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(`${value}T${time}`);

  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

export const toDateStartBoundary = (value) =>
  toDateBoundary(value, "00:00:00.000");

export const toDateEndBoundary = (value) =>
  toDateBoundary(value, "23:59:59.999");

export const isActivityOverdue = (activity, referenceDate = new Date()) => {
  if (activity?.status !== "pending" || !activity?.dueAt) {
    return false;
  }

  const dueAt = new Date(activity.dueAt);
  const now =
    referenceDate instanceof Date ? referenceDate : new Date(referenceDate);

  if (Number.isNaN(dueAt.getTime()) || Number.isNaN(now.getTime())) {
    return false;
  }

  return dueAt.getTime() < now.getTime();
};

export const isDealNextActionOverdue = (deal, referenceDate = new Date()) =>
  deal?.status === "open" &&
  isActivityOverdue(
    {
      status: "pending",
      dueAt: deal?.nextActionAt,
    },
    referenceDate,
  );

export const buildCrmBoardData = ({
  stages = [],
  deals = [],
  makeCardMetadata,
} = {}) => {
  const groupedDeals = groupDealsByStage(deals);

  return {
    lanes: sortByOrderAndId(stages).map((stage) => {
      const stageDeals = groupedDeals[String(stage.id)] || [];

      return {
        id: String(stage.id),
        title: stage.name,
        label: String(stageDeals.length),
        metadata: {
          stage,
        },
        cards: stageDeals.map((deal) => ({
          id: String(deal.id),
          title: deal.title,
          label: deal.contact?.name || "Sem contato",
          metadata:
            typeof makeCardMetadata === "function"
              ? makeCardMetadata(deal, stage)
              : { deal, stage },
          draggable: deal.status === "open" && stage.isActive !== false,
        })),
      };
    }),
  };
};

export const summarizeDeals = (deals = []) =>
  deals.reduce(
    (summary, deal) => {
      const amount = Number(deal?.amount || 0);

      return {
        total: summary.total + 1,
        open: summary.open + (deal?.status === "open" ? 1 : 0),
        won: summary.won + (deal?.status === "won" ? 1 : 0),
        lost: summary.lost + (deal?.status === "lost" ? 1 : 0),
        amount: summary.amount + amount,
      };
    },
    {
      total: 0,
      open: 0,
      won: 0,
      lost: 0,
      amount: 0,
    },
  );
